import { useEffect, useState } from "react";
import client, { fmt } from "@/lib/api";
import { Panel } from "@/components/Layout";
import { Switch } from "@/components/ui/switch";
import { Crown, Users, Sparkles, ArrowDown } from "lucide-react";
import { toast } from "sonner";

export default function Wallet() {
  const [config, setConfig] = useState(null);
  const [totals, setTotals] = useState(null);
  const [dists, setDists] = useState([]);
  const [form, setForm] = useState({ source_type: "policy", gross_amount: "", description: "" });

  const load = async () => {
    const [c, t, d] = await Promise.all([
      client.get("/wallet/config"),
      client.get("/wallet/totals"),
      client.get("/wallet/distributions"),
    ]);
    setConfig(c.data);
    setTotals(t.data);
    setDists(d.data);
  };

  useEffect(() => { load(); }, []);

  const toggleLineage = async (val) => {
    const { data } = await client.post(`/wallet/lineage?enabled=${val}`);
    setConfig(data);
    toast.success(val ? "75% Lineage engaged — Admiral override" : "Standard 60/30/10 restored");
  };

  const distribute = async (e) => {
    e.preventDefault();
    if (!form.gross_amount) return toast.error("Enter amount");
    try {
      await client.post("/wallet/distribute", { ...form, gross_amount: parseFloat(form.gross_amount) || 0 });
      toast.success("Distribution executed");
      setForm({ source_type: "policy", gross_amount: "", description: "" });
      load();
    } catch {
      toast.error("Distribution failed");
    }
  };

  if (!config || !totals) return <div className="text-[#888]">Booting wallet engine...</div>;

  const admiralPct = config.lineage_enabled ? config.lineage_share : config.admiral_share;
  const remainder = 100 - admiralPct;
  const fleetPct = config.lineage_enabled ? +(remainder * 0.75).toFixed(2) : config.fleet_share;
  const cyberPct = config.lineage_enabled ? +(remainder - fleetPct).toFixed(2) : config.cyberkids_share;

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag mb-2">/// MODULE 03 — SOVEREIGN REVENUE ENGINE</div>
          <h1 className="heading-display text-4xl sm:text-5xl">60 / 30 / 10 Wallet</h1>
        </div>
        <div className="hud-panel p-4 flex items-center gap-4" data-testid="lineage-toggle-container">
          <div className="hud-corner-tl" /><div className="hud-corner-tr" />
          <div className="hud-corner-bl" /><div className="hud-corner-br" />
          <div className="relative z-10">
            <div className="label-tag text-gold">Lineage Override</div>
            <div className="text-xs text-[#888] mt-1">75% Admiral share when engaged</div>
          </div>
          <Switch
            checked={config.lineage_enabled}
            onCheckedChange={toggleLineage}
            data-testid="lineage-switch"
            className="relative z-10 data-[state=checked]:bg-gold"
          />
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="split-cards">
        <SplitCard icon={Crown} label="Admiral Akanksha" pct={admiralPct} amount={totals.admiral_total} accent testId="split-admiral" />
        <SplitCard icon={Users} label="Fleet" pct={fleetPct} amount={totals.fleet_total} testId="split-fleet" />
        <SplitCard icon={Sparkles} label="Cyberkids" pct={cyberPct} amount={totals.cyberkids_total} testId="split-cyber" />
      </section>

      <Panel tag="// EXECUTE" title="New Distribution" testId="wallet-distribute">
        <form onSubmit={distribute} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="distribute-form">
          <div>
            <label className="label-tag block mb-1">Source</label>
            <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })}
              className="w-full bg-obsidian border border-[#332A0C] px-3 py-2 focus:border-gold outline-none" data-testid="dist-source">
              <option value="policy">Policy Commission</option>
              <option value="referral">Referral Payout</option>
              <option value="manual">Manual Entry</option>
            </select>
          </div>
          <div>
            <label className="label-tag block mb-1">Amount (USD)</label>
            <input required type="number" step="0.01" value={form.gross_amount}
              onChange={(e) => setForm({ ...form, gross_amount: e.target.value })}
              className="w-full bg-obsidian border border-[#332A0C] px-3 py-2 focus:border-gold outline-none mono"
              placeholder="0.00" data-testid="dist-amount" />
          </div>
          <div className="md:col-span-2">
            <label className="label-tag block mb-1">Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-obsidian border border-[#332A0C] px-3 py-2 focus:border-gold outline-none"
              placeholder="e.g. Commercial policy commission" data-testid="dist-desc" />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button type="submit" className="btn-gold inline-flex items-center gap-2" data-testid="distribute-submit">
              <ArrowDown className="w-4 h-4" /> Execute Distribution
            </button>
          </div>
        </form>
      </Panel>

      <Panel tag={`// LEDGER — ${dists.length} ENTRIES`} title="Distribution Ledger" testId="wallet-ledger">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead>
              <tr className="border-b border-gold/40">
                <th className="text-left label-tag py-3">Time</th>
                <th className="text-left label-tag py-3">Source</th>
                <th className="text-left label-tag py-3">Description</th>
                <th className="text-right label-tag py-3">Gross</th>
                <th className="text-right label-tag py-3">Admiral</th>
                <th className="text-right label-tag py-3">Fleet</th>
                <th className="text-right label-tag py-3">Cyberkids</th>
                <th className="text-right label-tag py-3">Mode</th>
              </tr>
            </thead>
            <tbody>
              {dists.map((d) => (
                <tr key={d.id} className="border-b border-[#1a1a1a]" data-testid={`ledger-row-${d.id}`}>
                  <td className="py-2 mono text-xs text-[#888]">{fmt.shortDate ? fmt.shortDate(d.distributed_at) : d.distributed_at}</td>
                  <td className="py-2 label-tag text-gold">{d.source_type}</td>
                  <td className="py-2">{d.description || "—"}</td>
                  <td className="py-2 text-right mono font-bold">{fmt.money(d.gross_amount)}</td>
                  <td className="py-2 text-right mono text-gold">{fmt.money(d.admiral_amount)}</td>
                  <td className="py-2 text-right mono">{fmt.money(d.fleet_amount)}</td>
                  <td className="py-2 text-right mono">{fmt.money(d.cyberkids_amount)}</td>
                  <td className="py-2 text-right">
                    <span className={`label-tag px-2 py-1 border ${d.lineage_active ? "border-gold text-gold" : "border-[#332A0C] text-[#888]"}`}>
                      {d.lineage_active ? "LINEAGE" : "60/30/10"}
                    </span>
                  </td>
                </tr>
              ))}
              {dists.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-[#666]">No distributions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function SplitCard({ icon: Icon, label, pct, amount, accent, testId }) {
  return (
    <div className={`hud-panel p-6 relative ${accent ? "border-gold" : ""}`} data-testid={testId}>
      <div className="hud-corner-tl" /><div className="hud-corner-tr" />
      <div className="hud-corner-bl" /><div className="hud-corner-br" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <Icon className={`w-7 h-7 ${accent ? "text-gold" : "text-[#888]"}`} />
          <div className={`label-tag ${accent ? "text-gold" : ""}`}>{label}</div>
        </div>
        <div className="metric-value text-5xl mb-1">
          <span className={accent ? "text-gold" : "text-white"}>{pct}</span><span className="text-2xl text-[#666]">%</span>
        </div>
        <div className="label-tag text-[#666] mb-3">Share allocation</div>
        <div className="risk-bar mb-3">
          <div className="risk-bar-fill" style={{ width: `${pct}%`, background: accent ? "#D4AF37" : "#666" }} />
        </div>
        <div className="flex items-baseline justify-between border-t border-[#332A0C] pt-3">
          <span className="label-tag">Lifetime</span>
          <span className="mono font-bold text-lg">{fmt.money(amount)}</span>
        </div>
      </div>
    </div>
  );
}
